import { StandardLineItem } from '@/types/ShopFront/CheckoutStandards'
import type { ProductType } from '@/types'
import { error, log } from '@/services/Log'
import allPromisesWithRetries from './allPromisesWithRetries'
import { checkIsExtendItem } from './checkIsExtendItem'
import { getCategoryName } from './getCategoryName'

type CategoryType = { name: string, path: string }

type productGAInput = {
  name: string,
  sku: string,
  price: string,
  salePrice: number,
  variantId: number,
  categories: CategoryType[],
  event: string,
  brand: string,
  quantity?: number,
  customerId?: number,
  customerEmail?: string,
  cartTotal: number
}

const getActualPrice = (item: {
  salePrice: number
  couponAmount: number,
  discounts: { discountedAmount: number }[]
  quantity: number
}, purchase: boolean) => {
  let price = item.salePrice
  if (item?.discounts?.length) {
    const coupon = purchase ? item.couponAmount : item.discounts[0].discountedAmount
    price = parseFloat((item.salePrice - (coupon / item.quantity)).toFixed(2))
  }
  return price
}

const categoryNamesSorter = (a: CategoryType, b: CategoryType) => {
  const pathDiff = b.path.split('/').length - a.path.split('/').length
  return pathDiff || (b.name.length - a.name.length)
}

export const getBrandName = async (lineItems?: StandardLineItem[]) => {
  const skus = lineItems?.filter(({ sku }) => (
    !!sku && !checkIsExtendItem({ sku })
  )).map((item) => item.sku)
  const [{ Product }] = await allPromisesWithRetries(() => [import('@/services/Product')])
  const skusData = await Product.bySkus(skus ?? [])
  const itemMetadata: { [key: string]: ProductType } = {}
  skusData.forEach((p) => {
    itemMetadata[p.id] = p
    itemMetadata[p.sku] = p
    p.variants.forEach((v) => {
      itemMetadata[v.sku] = p
    })
  })
  return (sku: string) => itemMetadata?.[sku]?.brand || ''
}

export const gaProducts = async (lineItems?: StandardLineItem[], purchase = false) => {
  const itemMetadata: { [key: string]: ProductType } = {}
  const categoryName = (sku: string) => [
    ...(itemMetadata[sku]?.category_info || []),
  ].sort(categoryNamesSorter)?.[0]?.name
  const brandName = await getBrandName(lineItems)
  return lineItems?.map((item) => ({ // List of productFieldObjects.
    name: item.name,
    id: item.sku,
    salePrice: item.salePrice, // original price paid for product before discount
    price: getActualPrice(item, purchase), // net price paid for product per quantity
    brand: brandName(item?.sku),
    category: item?.productId ? categoryName(item?.sku) : '',
    variant: item.variantId, // "Small" | "Large" | etc
    quantity: item.quantity,
  }))
}

export const sendEventToGA = async (
  lineItems? : StandardLineItem[],
  couponCode? : string,
  pageName? : string,
  eventName? : 'couponApplied' | 'addedCreditCard' | 'addedShipping',
  cartTotal? : number,
) => {
  const sendToGA = async () => {
    const [
      { getCustomerDetails },
    ] = await allPromisesWithRetries(() => [
      import('@/helpers/common'),
    ])
    log('sending GA event', eventName)
    try {
      const customer = getCustomerDetails()
      const customerId = customer?.customerId
      const customerEmail = customer?.customerEmail
      const visitorType = customerEmail ? 'Logged In' : 'Guest'
      window.dataLayer?.push({
        event: eventName,
        ecommerce: {
          currencyCode: 'USD',
          checkout: {
            actionField: { couponCode },
            products: await gaProducts(lineItems),
            total: cartTotal,
          },
        },
        customerEmail,
        visitorType,
        customerId,
        pageName,
      })
    } catch (error) {
      console.error(error)
    }
  }
  if (window.loadedAndInteractive) {
    await sendToGA()
  } else {
    window.addEventListener('loadedAndInteractive', sendToGA as () => void, { once: true })
  }
}

export const productGA: (i: productGAInput) => void = ({
  name,
  sku,
  price,
  salePrice,
  variantId,
  categories,
  event,
  brand,
  quantity,
  customerId = 0,
  customerEmail = '',
  cartTotal,
}) => {
  const sendToGA = () => {
    try {
      log('productGA recording', {
        name,
        sku,
        price,
        salePrice,
        variantId,
        categories,
        event,
        brand,
        quantity,
        customerId,
        customerEmail,
        cartTotal,
      })
      const keyName = (event === 'addToCart') ? 'add' : 'detail'
      const dataLayerEvent = {
        event,
        ecommerce: {
          currencyCode: 'USD',
          [keyName]: {
            products: [{
              name,
              id: sku, // for grouped products like quilts, do you have a default that you can use?
              price, // for grouped products like quilts, do you have a default that you can use?
              // for grouped products like quilts, do you have a default that you can use?
              salePrice,
              brand,
              variant: variantId,
              category: getCategoryName(categories), // category visited in from
              totalReviews: '',
              averageRating: '',
              inventoryCount: 'In Stock',
              quantity: `${typeof quantity !== 'undefined' ? quantity : ''}`,
            }],
          },
        },
        cartTotal,
        customerId,
        customerEmail,
        visitorType: customerEmail ? 'Logged In' : 'Guest',
        pageName: 'pdp',
      }
      window?.dataLayer?.push({ ecommerce: null }) // Clear the previous ecommerce object.
      window?.dataLayer?.push(dataLayerEvent)
    } catch (err) {
      error(`productGA Failed ${String(err)}`)
    }
  }
  if (window.loadedAndInteractive) {
    sendToGA()
  } else {
    window.addEventListener('loadedAndInteractive', sendToGA, { once: true })
  }
}
